import React, { useState } from 'react';
import { 
  X, 
  Lock, 
  ShieldCheck, 
  AlertCircle, 
  User, 
  Mail, 
  KeyRound, 
  CheckCircle, 
  ArrowRight, 
  RefreshCw,
  Smartphone,
  CheckSquare,
  Square
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';

const AdminLoginModal = ({ isOpen, onClose, onSuccess }) => {
  const { lang, t } = useLanguage();
  const { loginAdmin, verifyTwoFactorCode } = useStore();
  
  // Step: 'credentials' | '2fa' | 'forgot'
  const [modalMode, setModalMode] = useState('credentials'); 

  // Credentials State
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // 2FA State
  const [twoFactorData, setTwoFactorData] = useState(null); // { tempToken, twoFactorType, message }
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState(null);

  // Password Recovery States
  const [forgotStep, setForgotStep] = useState(1); // 1: Request Code, 2: Enter Code & New Pass
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState(null);
  const [forgotSuccess, setForgotSuccess] = useState(null);

  if (!isOpen) return null;

  // Helper: Resolve API Endpoint
  const getApiEndpoint = (file, params = {}) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const isSubfolder = typeof window !== 'undefined' && window.location.pathname.includes('/himalayan');
    const basePath = isSubfolder ? `/himalayan/api/${file}` : `/api/${file}`;
    const url = new URL(`${origin}${basePath}`);
    Object.keys(params).forEach(k => url.searchParams.set(k, params[k]));
    return url.toString();
  };

  // 1. Submit Primary Login Form (Username + Password)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('براہ کرم یوزر نیم اور پاس ورڈ دونوں درج کریں۔');
      return;
    }

    setIsLoading(true);
    setError(null);

    const result = await loginAdmin(username.trim(), password.trim());
    setIsLoading(false);

    if (result && result.requires2fa) {
      // 2FA Triggered!
      setTwoFactorData(result);
      setTwoFactorCode('');
      setTwoFactorError(null);
      setModalMode('2fa');
    } else if (result && result.success) {
      setError(null);
      setUsername('');
      setPassword('');
      onSuccess();
    } else {
      setError(result?.message || 'غلط یوزر نیم یا پاس ورڈ! دوبارہ کوشش کریں۔');
    }
  };

  // 2. Submit 2FA Verification (OTP or Authenticator TOTP)
  const handleTwoFactorSubmit = async (e) => {
    e.preventDefault();
    if (!twoFactorCode.trim() || twoFactorCode.trim().length < 6) {
      setTwoFactorError('براہ کرم 6 ہندسوں کا سیکیورٹی کوڈ درج کریں۔');
      return;
    }

    setTwoFactorLoading(true);
    setTwoFactorError(null);

    const res = await verifyTwoFactorCode(twoFactorData?.tempToken, twoFactorCode.trim(), rememberDevice);
    setTwoFactorLoading(false);

    if (res && res.success) {
      setModalMode('credentials');
      setTwoFactorData(null);
      setTwoFactorCode('');
      setUsername('');
      setPassword('');
      onSuccess();
    } else {
      setTwoFactorError(res?.message || '2FA کوڈ غلط یا زائد المیعاد ہے۔');
    }
  };

  // 3. Request 6-Digit Reset Code via Email
  const handleRequestResetCode = async () => {
    setForgotLoading(true);
    setForgotError(null);
    setForgotSuccess(null);

    try {
      const resetUrl = getApiEndpoint('forgot_password.php', { action: 'request' });
      const res = await fetch(resetUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'request' })
      });

      const text = await res.text();
      let data = null;
      try {
        data = JSON.parse(text);
      } catch (e) {}

      if (data && data.success) {
        setForgotStep(2);
        setForgotSuccess(data.message || '6 ہندسوں کا ویریفکیشن کوڈ رجسٹرڈ ایڈمن ای میلز پر بھیج دیا گیا ہے۔');
      } else {
        setForgotError(data?.error || 'سرور سے ای میل نہیں بھیجی جا سکی۔ براہ کرم سرور سیٹنگز چیک کریں۔');
      }
    } catch (err) {
      setForgotError('سرور سے رابطہ قائم نہیں ہو سکا۔ براہ کرم انٹرنیٹ یا ہوسٹنگر کا کنکشن چیک کریں۔');
    } finally {
      setForgotLoading(false);
    }
  };

  // 4. Verify Code on Server & Set New Password
  const handleVerifyAndReset = async (e) => {
    e.preventDefault();
    if (!resetCode.trim()) {
      setForgotError('براہ کرم 6 ہندسوں کا ویریفکیشن کوڈ درج کریں۔');
      return;
    }
    if (!newPassword.trim() || newPassword.trim().length < 4) {
      setForgotError('نیا پاس ورڈ کم از کم 4 حروف پر مشتمل ہونا چاہیے۔');
      return;
    }

    setForgotLoading(true);
    setForgotError(null);
    setForgotSuccess(null);

    try {
      const resetUrl = getApiEndpoint('forgot_password.php', { action: 'reset' });
      const res = await fetch(resetUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'reset',
          code: resetCode.trim(),
          new_password: newPassword.trim()
        })
      });

      const text = await res.text();
      let data = null;
      try {
        data = JSON.parse(text);
      } catch (e) {}

      if (data && data.success) {
        setForgotSuccess(data.message || 'پاس ورڈ کامیابی سے تبدیل ہو گیا ہے۔ اب نئے پاس ورڈ کے ساتھ لاگ ان کریں۔');
        if (data.username) {
          setUsername(data.username);
        }
        setTimeout(() => {
          setModalMode('credentials');
          setForgotStep(1);
          setResetCode('');
          setNewPassword('');
          setForgotError(null);
          setForgotSuccess(null);
        }, 2200);
      } else {
        setForgotError(data?.error || 'ویریفکیشن کوڈ غلط ہے یا ایکسپائر ہو چکا ہے۔');
      }
    } catch (err) {
      setForgotError('سرور سے رابطہ قائم نہیں ہو سکا۔ براہ کرم دوبارہ کوشش کریں۔');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div 
        className="relative bg-white w-full max-w-sm rounded-3xl shadow-2xl p-6 border border-gray-100 font-urdu"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 left-4 p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* ------------------------------------------------------------- */}
        {/* MODE 1: PRIMARY CREDENTIALS (USERNAME + PASSWORD) */}
        {/* ------------------------------------------------------------- */}
        {modalMode === 'credentials' && (
          <>
            <div className="text-center space-y-2 mb-6">
              <div className="w-14 h-14 bg-emerald-100 text-emerald-800 rounded-2xl flex items-center justify-center mx-auto shadow-inner">
                <Lock className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  ایڈمن لاگ اِن
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  ہمالین پنسار سٹور کنٹرول پینل
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  یوزر نیم:
                </label>
                <div className="relative">
                  <input
                    type="text"
                    autoFocus
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setError(null);
                    }}
                    placeholder="admin یا اپنا یوزر نیم"
                    className={`w-full p-3 rounded-xl border text-center text-sm font-sans dir-ltr ${
                      error ? 'border-rose-400 bg-rose-50' : 'border-gray-200 focus:border-emerald-700'
                    } focus:outline-none`}
                  />
                  <User className="w-4 h-4 text-gray-400 absolute left-3 top-3.5" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-bold text-gray-700">
                    پاس ورڈ:
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setModalMode('forgot');
                      setError(null);
                      setForgotError(null);
                      setForgotSuccess(null);
                    }}
                    className="text-[11px] text-emerald-700 hover:text-emerald-900 underline transition-colors cursor-pointer font-bold"
                  >
                    پاس ورڈ بھول گئے؟
                  </button>
                </div>
                <div className="relative">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                    placeholder="پاس ورڈ درج کریں..."
                    className={`w-full p-3 rounded-xl border text-center text-base tracking-widest font-mono ${
                      error ? 'border-rose-400 bg-rose-50' : 'border-gray-200 focus:border-emerald-700'
                    } focus:outline-none`}
                  />
                  <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-3.5" />
                </div>

                {error && (
                  <div className="text-xs text-rose-600 mt-2 text-center flex items-center justify-center gap-1.5 bg-rose-50 p-2.5 rounded-xl border border-rose-200">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white font-bold text-sm shadow-md transition disabled:opacity-50 cursor-pointer flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>تصدیق ہو رہی ہے...</span>
                  </>
                ) : (
                  <span>ڈیش بورڈ میں داخل ہوں</span>
                )}
              </button>

              <div className="pt-2 text-center text-[11px] text-gray-400 border-t border-gray-100">
                🔒 سرور سائیڈ Bcrypt انکرپشن اور 2FA پروٹیکشن فعال ہے۔
              </div>
            </form>
          </>
        )}

        {/* ------------------------------------------------------------- */}
        {/* MODE 2: TWO-FACTOR AUTHENTICATION (2FA OTP / TOTP) */}
        {/* ------------------------------------------------------------- */}
        {modalMode === '2fa' && (
          <div className="space-y-4 animate-fade-in">
            <div className="text-center space-y-2 mb-4">
              <div className="w-14 h-14 bg-emerald-100 text-emerald-800 rounded-2xl flex items-center justify-center mx-auto shadow-inner">
                {(twoFactorData?.twoFactorType === 'totp' || twoFactorData?.twoFactorType === 'authenticator_app') ? (
                  <Smartphone className="w-7 h-7" />
                ) : (
                  <ShieldCheck className="w-7 h-7" />
                )}
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                ٹو فیکٹر اتھینٹیکیشن (2FA)
              </h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                {(twoFactorData?.twoFactorType === 'totp' || twoFactorData?.twoFactorType === 'authenticator_app')
                  ? 'اپنے موبائل کی Google Authenticator ایپ سے 6 ہندسوں کا سیکیورٹی کوڈ درج کریں۔'
                  : 'آپ کے رجسٹرڈ ایڈمن ای میل پر 6 ہندسوں کا لاگ ان سیکیورٹی کوڈ بھیج دیا گیا ہے۔'}
              </p>
            </div>

            <form onSubmit={handleTwoFactorSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1 text-center">
                  6 ہندسوں کا 2FA کوڈ درج کریں:
                </label>
                <input
                  type="text"
                  maxLength={6}
                  autoFocus
                  value={twoFactorCode}
                  onChange={(e) => {
                    setTwoFactorCode(e.target.value);
                    setTwoFactorError(null);
                  }}
                  placeholder="مثلاً: 839210"
                  className="w-full p-3 rounded-xl border border-gray-300 text-center text-xl font-mono tracking-widest focus:border-emerald-700 focus:outline-none bg-slate-50"
                />
              </div>

              {/* 30-Day Trusted Device Checkbox */}
              <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none bg-emerald-50/70 p-2.5 rounded-xl border border-emerald-200/80">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="w-4 h-4 accent-emerald-700 rounded cursor-pointer"
                />
                <span className="font-urdu text-[11px] font-bold text-emerald-950">
                  اس ڈیوائس کو اگلے 30 دن تک یاد رکھیں (30-Day Trust)
                </span>
              </label>

              {twoFactorError && (
                <div className="text-xs text-rose-700 bg-rose-50 p-2.5 rounded-xl border border-rose-200 text-center flex items-center justify-center gap-1.5">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{twoFactorError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={twoFactorLoading}
                className="w-full py-3 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white font-bold text-sm shadow-md transition disabled:opacity-50 cursor-pointer flex items-center justify-center gap-2"
              >
                {twoFactorLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>2FA تصدیق ہو رہی ہے...</span>
                  </>
                ) : (
                  <span>2FA تصدیق اور لاگ اِن</span>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setModalMode('credentials');
                  setTwoFactorData(null);
                  setTwoFactorCode('');
                  setTwoFactorError(null);
                }}
                className="w-full text-center text-xs text-gray-500 hover:text-gray-800 pt-1 cursor-pointer font-bold"
              >
                ← واپس پاس ورڈ درج کریں
              </button>
            </form>
          </div>
        )}

        {/* ------------------------------------------------------------- */}
        {/* MODE 3: FORGOT PASSWORD (OTP RECOVERY) */}
        {/* ------------------------------------------------------------- */}
        {modalMode === 'forgot' && (
          <div className="space-y-4 animate-fade-in">
            <div className="text-center space-y-2 mb-4">
              <div className="w-12 h-12 bg-amber-100 text-amber-800 rounded-2xl flex items-center justify-center mx-auto shadow-inner">
                <KeyRound className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                پاس ورڈ بحالی (Password Recovery)
              </h3>
              <p className="text-xs text-gray-500">
                سیکیورٹی تصدیقی کوڈ رجسٹرڈ ایڈمن ای میلز پر بھیجا جائے گا۔
              </p>
            </div>

            {forgotStep === 1 ? (
              <div className="space-y-3">
                <p className="text-xs text-slate-600 leading-relaxed text-center">
                  اپنے رجسٹرڈ ایڈمن ای میل پر 6 ہندسوں کا سیکیورٹی OTP کوڈ حاصل کرنے کے لیے نیچے دیے گئے بٹن پر کلک کریں۔
                </p>

                <button
                  type="button"
                  onClick={handleRequestResetCode}
                  disabled={forgotLoading}
                  className="w-full py-3 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white font-bold text-xs shadow transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {forgotLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>کوڈ بھیجا جا رہا ہے...</span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      <span>ای میل پر ویریفکیشن کوڈ حاصل کریں</span>
                    </>
                  )}
                </button>
              </div>
            ) : (
              <form onSubmit={handleVerifyAndReset} className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    ای میل پر موصول شدہ 6 ہندسوں کا کوڈ:
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    autoFocus
                    value={resetCode}
                    onChange={(e) => {
                      setResetCode(e.target.value);
                      setForgotError(null);
                    }}
                    placeholder="مثلاً: 582914"
                    className="w-full p-2.5 rounded-xl border border-gray-300 text-center text-lg font-mono tracking-widest focus:border-emerald-700 focus:outline-none bg-slate-50"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    نیا پاس ورڈ درج کریں:
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setForgotError(null);
                    }}
                    placeholder="نیا پاس ورڈ لکھیں..."
                    className="w-full p-2.5 rounded-xl border border-gray-300 text-center font-mono focus:border-emerald-700 focus:outline-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full py-2.5 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white font-bold text-xs shadow transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {forgotLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>تصدیق اور اپ ڈیٹ ہو رہا ہے...</span>
                    </>
                  ) : (
                    <span>پاس ورڈ تبدیل کریں</span>
                  )}
                </button>

                <div className="text-center pt-1">
                  <button
                    type="button"
                    onClick={handleRequestResetCode}
                    disabled={forgotLoading}
                    className="text-[11px] text-emerald-700 hover:text-emerald-900 underline cursor-pointer"
                  >
                    دوبارہ نیا کوڈ بھیجیں
                  </button>
                </div>
              </form>
            )}

            {forgotError && (
              <div className="text-xs text-rose-700 bg-rose-50 p-2.5 rounded-xl border border-rose-200 text-center flex items-center justify-center gap-1.5">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{forgotError}</span>
              </div>
            )}

            {forgotSuccess && (
              <div className="text-xs text-emerald-800 bg-emerald-50 p-2.5 rounded-xl border border-emerald-200 text-center flex items-center justify-center gap-1.5">
                <CheckCircle className="w-4 h-4 shrink-0" />
                <span>{forgotSuccess}</span>
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                setModalMode('credentials');
                setForgotStep(1);
                setForgotError(null);
                setForgotSuccess(null);
              }}
              className="w-full text-center text-xs text-gray-500 hover:text-gray-800 pt-2 cursor-pointer font-bold"
            >
              ← واپس لاگ ان فارم پر جائیں
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLoginModal;
